import {
  DEFAULT_OVERLAY,
  OVERLAY_LIMITS,
} from "@cuekards/core/overlay-defaults";
import { MID_TAKE_REASON } from "./control-bar.js";

/**
 * What the prompter screen decides, as against what it draws (web-ui ticket 08).
 *
 * The same split `control-bar.ts` and `green-room.ts` are on either side of, for
 * the same reason: a screen whose whole content is a video, a scrim and one pill
 * has very little in it that a browser test would catch and a couple of rules
 * that are silent when they are wrong. Those rules are here, in Node, and the
 * geometry is in `prompter.browser.test.ts` where geometry belongs.
 */

/**
 * How much stronger the reading band is than the flat wash behind it.
 *
 * Derived rather than dialled, and that is the decision rather than the number.
 * "How dark behind the words" and "how dark behind the line you are on" is one
 * more question than anybody wants to answer about their own face, and the
 * second answer only ever means "a bit more than the first". A quarter again is
 * enough for white type to hold over a lit cheek without the band reading as a
 * bar drawn across the picture.
 *
 * It multiplies what is left rather than the value — `x + (100 - x) / 4` rather
 * than `x * 1.25` — so the top of the range stays short of opaque. That is the
 * same argument {@link OVERLAY_LIMITS.maxBackdrop} is 90 for, and a band that
 * scaled past it would quietly undo it.
 */
const BAND_SHARE = 0.25;

/**
 * How tall the reading band is, and how far below the anchor its centre sits —
 * both as percentages of the prompter's height (text-align ticket 01).
 *
 * These two numbers, and only these two, are what turns an anchor into a band.
 * They were derived once by hand against an anchor of a third and typed into
 * `web.css` as `26%` and `46%`, back when the anchor could not move; the
 * stylesheet's comment said the band was centred a little below the anchor, and
 * this is that sentence written where it can be evaluated.
 *
 * Below rather than on, because the script flows *downward* from the reading
 * line: the words the eye is about to reach are under it, and a band centred on
 * the line would fall away exactly where the next sentence is.
 */
const BAND_HEIGHT = 20;
const BAND_DROP = 3;

/** The scrim: the two strengths the stylesheet composes it from, and the two
 * positions it composes them at. */
export interface BackdropStyle {
  "--web-backdrop": string;
  "--web-backdrop-band": string;
  "--web-backdrop-band-top": string;
  "--web-backdrop-band-bottom": string;
}

/**
 * The Backdrop setting as something a rule can mix with.
 *
 * Custom properties rather than a whole `background`, because the scrim is two
 * layers and composing both here would put the gradient's *shape* — how it falls
 * away at either end — somewhere no stylesheet can see it. What crosses the
 * boundary is the strength, which is the only part of it that is a preference,
 * and the band's two edges, which are the only part of it that moves.
 *
 * **The edges are not a preference either**, and the rule survives this ticket
 * intact: they are where the anchor is, and the anchor is a fact about the
 * reader's desk. The band was always derived from it — it was just derived once
 * by hand and typed into `web.css` as `26%` and `46%`, which was fine for
 * exactly as long as the anchor could not move.
 *
 * Total in both arguments, like the store's own sanitiser and for the same
 * reason: this is reached from a live subscription, and a `NaN` arriving from a
 * hand-edited settings file must not be the thing that makes a scrim disappear
 * mid-take. The clamps are a repeat of `sanitiseOverlayDefaults`' rather than a
 * second opinion about it — the wash reads {@link OVERLAY_LIMITS} and the anchor
 * is a fraction of the prompter, so its range is the whole of it — and they are
 * here because both values can reach this before they have been through the
 * store at all.
 */
export function backdropStyle(backdrop: number, anchor: number): BackdropStyle {
  const wash = Number.isFinite(backdrop)
    ? Math.min(
        Math.max(backdrop, OVERLAY_LIMITS.minBackdrop),
        OVERLAY_LIMITS.maxBackdrop,
      )
    : DEFAULT_OVERLAY.backdrop;
  // Rounded, so the property is a value somebody reading the computed style can
  // recognise as the one on the slider.
  const band = Math.round(wash + (100 - wash) * BAND_SHARE);
  /* Rounded before anything is derived from it, because a stored `0.33` is
   * `33.000000000000004` once it is a percentage, and the two stops the default
   * has to keep rendering are whole numbers. */
  const line = Math.round(
    (Number.isFinite(anchor)
      ? Math.min(Math.max(anchor, 0), 1)
      : DEFAULT_OVERLAY.anchor) * 100,
  );
  const centre = line + BAND_DROP;
  return {
    "--web-backdrop": `${wash}%`,
    // Zero means zero: a band still lit at the floor of the slider would make
    // the bottom of the range a wash the user cannot take off, which is not what
    // a zero on a control says.
    "--web-backdrop-band": `${wash === 0 ? 0 : band}%`,
    /* Allowed off either end of the element, and deliberately not clamped to
     * `0%`/`100%`: a gradient stop outside the box is legal CSS and means the
     * band is half off the screen, which is exactly what an anchor at the very
     * top is asking for. Clamping would squash the band instead of moving it. */
    "--web-backdrop-band-top": `${centre - BAND_HEIGHT / 2}%`,
    "--web-backdrop-band-bottom": `${centre + BAND_HEIGHT / 2}%`,
  };
}

/** The top bar's left-hand pill: live, or refused with a reason. */
export interface TopBarButton {
  disabled: boolean;
  /** The `title`, which is the only place a dimmed control explains itself. */
  reason: string;
}

export interface TopBarInputs {
  takeInProgress: boolean;
}

/**
 * The button that walks off the prompter, and whether it may.
 *
 * It is the `⋯` menu's `edit` row in front of the menu, so it answers to the
 * same rule rather than to a copy of it: {@link MID_TAKE_REASON}, said once, for
 * a take you walked off the screen to edit a script and have therefore lost.
 *
 * A `⟳ Refresh` pill stood beside it until flow ticket 07, and its rule
 * (`refreshAvailability`, FR-17, four answers) came through here for it. Refresh
 * is the `⋯` menu's row and a keyboard chord now, both of which read that helper
 * directly — which is why nothing in this file has an opinion about it any more.
 *
 * The door stays live with nothing loaded, which is deliberate rather than an
 * omission: the way back to the green room must not depend on there being a
 * script to go back to, and from this screen it is the only way back there is.
 *
 * A record of one, rather than a bare button: the name is still plural because
 * the bar is still a bar, and a second pill would be a field here rather than a
 * change of shape at every call site.
 */
export function topBarButtons({
  takeInProgress,
}: TopBarInputs): { edit: TopBarButton } {
  return {
    edit: takeInProgress
      ? { disabled: true, reason: MID_TAKE_REASON }
      : { disabled: false, reason: "Back to the green room" },
  };
}
